import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { AnalyticsAttributes, Events, track } from '@app/analytics';
import { useHeader } from '@app/shell/hooks';
import {
  getSalesforceLanguageCode,
  getUrlPrefix,
} from '@app/redux/localization/selectors';
import { BuyFlowStep, CheckoutFlowPage } from '@checkout/utils/Constants';
import { BsAction, BsContext } from '@app/transaction';

import { useCurrency } from '@app/currency';
import { UseCheckoutHeaderContentResult } from '../CheckoutHeaderProps';

const ContextToEventMap = {
  [BsContext.Buy]: Events.FAQBidClick,
  [BsContext.Sell]: Events.FAQAskClick,
};

const useCheckoutHeaderContent = (): UseCheckoutHeaderContentResult => {
  const { data } = useHeader();
  const { context, page, product, action, step, showSizeChart } = data;

  const helpLanguage = useSelector(getSalesforceLanguageCode);
  const urlPrefix = useSelector(getUrlPrefix);

  const dispatch = useDispatch();

  const { t } = useTranslation();
  const { currency } = useCurrency();

  const eventLabel = useMemo(() => {
    if (page) {
      return page;
    }

    switch (true) {
      case step === BuyFlowStep.SizeSelect && showSizeChart:
        return CheckoutFlowPage.SizeChartPage;

      case step === BuyFlowStep.SizeSelect:
        return CheckoutFlowPage.SizeSelectorPage;

      case step === BuyFlowStep.Review && action === BsAction.Buy:
        return CheckoutFlowPage.ReviewPurchaseScreen;

      case step === BuyFlowStep.Review && action === BsAction.Bid:
        return CheckoutFlowPage.ReviewBidScreen;

      case step === BuyFlowStep.Shipping:
        return CheckoutFlowPage.ShippingInfoScreen;

      case step === BuyFlowStep.Billing:
        return CheckoutFlowPage.BillingInfoScreen;

      case action === BsAction.Buy:
        return CheckoutFlowPage.BuyNowForm;

      case action === BsAction.Bid:
        return CheckoutFlowPage.PlaceBidForm;

      default:
        return '';
    }
  }, [action, step, page, showSizeChart]);

  const onLogoClick = useCallback(() => {
    if (context === BsContext.Buy) {
      dispatch(
        track({
          event: Events.CheckoutFlowExitStockXClick,
          label: <string>eventLabel,
          decorators: {
            product,
          },
        }),
      );
    }
  }, [context, dispatch, eventLabel, product]);

  const onFaqClick = () =>
    dispatch(
      track({
        event: context ? ContextToEventMap[context] : '',
        properties: {
          [AnalyticsAttributes.cdLocalCurrency]: currency,
        },
        decorators: {
          product,
        },
      }),
    );

  const buyHelp = `https://help.stockx.com/s/article/What-is-a-Bid-and-how-do-I-buy-on-StockX?language=${helpLanguage}`;
  const sellHelp = `https://help.stockx.com/s/article/What-is-an-Ask-and-how-do-I-sell-on-StockX?language=${helpLanguage}`;

  return { urlPrefix, t, onLogoClick, onFaqClick, buyHelp, sellHelp, context };
};

export default useCheckoutHeaderContent;
